import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Series } from '~/domain/dicom/series';
import { DomainError } from '~/domain/errors/DomainError';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetService } from '~/domain/services/structureSet/StructureSetService';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import {
    useSelectedPrimaryImage,
    useSelectedSecondaryImage,
} from '~/hooks/image/useSelectedImages';
import { useBumpDicomProcessorVersion } from '~/hooks/useDicomProcessorVersion';
import { flushGridContourEdits } from '~/hooks/gridContourFlush';
import { localization } from '~/i18n';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { selectedRegistrationState } from '~/state/atoms/registration/registrationAtoms';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';
import useSelectedPatient from './patient/useSelectedPatient';
import useViewportEnvironment from './useViewportEnvironment';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useRemoveDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';

export default function useDeleteDataset() {
    const patient = useSelectedPatient();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const primaryImage = useSelectedPrimaryImage();
    const secondaryImage = useSelectedSecondaryImage();
    const currentDose = useCurrentDose();
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const session = useSessionOrNull();
    const removeDoseDisplaySettings = useRemoveDoseDisplaySettings();
    const {
        removePrimaryImage,
        removeEnvironmentSecondaryImage,
        unloadDose,
        unloadStructureSet,
    } = useViewportEnvironment();
    const selectedRegistration = useRecoilValue(selectedRegistrationState);
    const setSelectedRegistration = useSetRecoilState(
        selectedRegistrationState
    );
    const { enqueueSnackbar } = useSnackbar();
    const bumpDicomProcessorVersion = useBumpDicomProcessorVersion();

    const getAffectedStructureSets = useCallback(
        (image: Image3D): StructureSet[] => {
            if (!patient) {
                return [];
            }
            return patient.getAffectedStructureSets(image);
        },
        [patient]
    );

    const deleteImage = useCallback(
        (image: Image3D) => {
            if (!patient) {
                return;
            }
            // Tracked patient with no session is a teardown race: there is no
            // command context to remove the entity from the aggregate, so
            // fail closed BEFORE any environment/cache side-effect. Doing the
            // cache strip while leaving the entity in the aggregate would
            // diverge the two representations; a re-established session would
            // then surface an image whose processor entry is gone. The delete
            // is recoverable on retry once the session is back.
            if (!session && patient.isTracked) {
                enqueueSnackbar(
                    'Cannot delete while the patient is loading or unloading. ' +
                        'Please try again.',
                    { variant: 'warning' }
                );
                return;
            }
            // A tile that shows the image goes with it, and the
            // Environment it holds is freed with every contour edit it
            // carries: those edits reach the domain first, or nothing is
            // deleted.
            const registry = getDatasetEnvironmentRegistry();
            if (
                !flushGridContourEdits(
                    registry,
                    localization.t('contouring.gridSaveFailed.imageNotDeleted')
                )
            ) {
                return;
            }
            if (secondaryImage?.id === image.id) {
                removeEnvironmentSecondaryImage();
            }
            if (primaryImage?.id === image.id) {
                removePrimaryImage();
            }
            // Resolve BEFORE removal so the series UID survives the delete.
            const seriesUid = image.seriesUid;
            if (session) {
                // With a session the aggregate is tracked, so the removal
                // must go through the command-wrapped service; a direct
                // patient.removeImage would call markChanged outside a
                // command() and throw.
                session.imageService.remove({ id: image.id });
            } else {
                // No session and (guarded above) no tracker → a direct
                // removal is safe: markChanged is a no-op without a tracker.
                patient.removeImage(image);
            }
            dicomProcessor.removeSeriesFromPatient(
                patient.patientId,
                seriesUid
            );
            // The grid lets go of the image too: its rows empty, and every
            // row that fused it loses that fusion.
            registry.unregister(seriesUid);
            bumpDicomProcessorVersion();
        },
        [
            patient,
            primaryImage,
            secondaryImage,
            dicomProcessor,
            bumpDicomProcessorVersion,
            session,
            enqueueSnackbar,
            removePrimaryImage,
            removeEnvironmentSecondaryImage,
        ]
    );

    const deleteRawSeries = useCallback(
        (series: Series) => {
            if (!patient) {
                return;
            }
            dicomProcessor.removeSeriesFromPatient(
                patient.patientId,
                series.seriesInstanceUID
            );
            // A raw series lives only in the processor cache — no domain
            // entity, so no changeset and no domain-version bump. Without
            // this signal the deleted row stays on screen.
            bumpDicomProcessorVersion();
        },
        [patient, dicomProcessor, bumpDicomProcessorVersion]
    );

    const deleteDose = useCallback(
        (dose: DoseMatrix) => {
            if (!patient) {
                return;
            }
            // Fail closed on the tracked-without-session teardown race, for
            // the same reason as deleteImage — before any unload/cache side
            // effect, so the aggregate and the DICOM store cannot diverge.
            if (!session && patient.isTracked) {
                enqueueSnackbar(
                    'Cannot delete while the patient is loading or unloading. ' +
                        'Please try again.',
                    { variant: 'warning' }
                );
                return;
            }
            // Unloading takes the dose off every Environment that draws it;
            // the grid's tiles would otherwise go on drawing a colorwash the
            // patient no longer has.
            if (currentDose?.id === dose.id) {
                unloadDose();
            }
            // The dose's display settings outlive the entity otherwise: they
            // are keyed by a per-session DoseId, so a surviving entry can
            // never be read back and only carries a deleted dose's
            // prescription forward for the lifetime of the app.
            removeDoseDisplaySettings(dose.id);
            // Resolve BEFORE removal so the SOP UID survives the delete.
            const sopUid = dose.sopInstanceUid;
            if (session) {
                // With a session the aggregate is tracked, so the removal
                // must go through the command-wrapped service; a direct
                // patient.removeDose would call markChanged outside a
                // command() and throw.
                session.doseService.remove({ id: dose.id });
            } else {
                // No session and (guarded above) no tracker → a direct
                // removal is safe: markChanged is a no-op without a tracker.
                patient.removeDose(dose);
            }
            dicomProcessor.removeSopObjectFromPatient(
                patient.patientId,
                sopUid
            );
            bumpDicomProcessorVersion();
        },
        [
            patient,
            currentDose,
            dicomProcessor,
            bumpDicomProcessorVersion,
            enqueueSnackbar,
            unloadDose,
            removeDoseDisplaySettings,
            session,
        ]
    );

    const deleteStructureSet = useCallback(
        (structureSet: StructureSet) => {
            if (!patient) {
                return;
            }
            // An approved set is a clinical sign-off. Deleting it locally
            // would delete the signed-off database row at the next save
            // (the database refuses that too, but fail here before the
            // session is mutated).
            if (structureSet.isApproved) {
                enqueueSnackbar(
                    'Cannot delete an approved structure set. ' +
                        'Unapprove it first.',
                    { variant: 'warning' }
                );
                return;
            }
            // Fail closed on the tracked-without-session teardown race, for
            // the same reason as deleteImage/deleteDose — before any unload or
            // cache side-effect. This delete is worse than those if left
            // unguarded: removeStructureSet cascades over plans/doses and now
            // calls markChanged inside those loops (patient.ts), which throws
            // outside a command(). The set would already be spliced out and a
            // referencing plan unlinked before the throw, leaving the aggregate
            // half-mutated with no changeset and the DICOM strip below skipped.
            if (!session && patient.isTracked) {
                enqueueSnackbar(
                    'Cannot delete while the patient is loading or unloading. ' +
                        'Please try again.',
                    { variant: 'warning' }
                );
                return;
            }
            const sopUid = structureSet.dicomStructureSet.sopInstanceUid;
            // The selected set's unsaved grid edits reach the domain first,
            // or nothing is deleted.
            if (currentStructureSetId === sopUid) {
                if (
                    !flushGridContourEdits(
                        getDatasetEnvironmentRegistry(),
                        localization.t(
                            'contouring.gridSaveFailed.structureSetNotDeleted'
                        )
                    )
                ) {
                    return;
                }
                unloadStructureSet();
            }
            if (session) {
                // Route through the service: it owns the batch AND the
                // disposal of every ROI's undo stack / AI baseline. Left
                // behind, those stacks are keyed by ROINumber alone and get
                // inherited by the next set that shares the numbers — one
                // Ctrl+Z would republish a DELETED set's contour.
                try {
                    session.structureSetService.remove({
                        id: structureSet.id,
                    });
                } catch (e) {
                    // The command-wrapped remove throws on a stale reference
                    // (already gone from the aggregate) instead of silently
                    // no-oping like the previous direct call. Tolerate it:
                    // warn and abort so we never strip the DICOM object out
                    // from under a still-present structure set. Non-domain
                    // errors fail fast.
                    if (e instanceof DomainError) {
                        enqueueSnackbar(e.message, { variant: 'warning' });
                        return;
                    }
                    throw e;
                }
            } else {
                // No session and (guarded above) no tracker → direct removal,
                // but the snapshots must still be disposed through the same
                // domain seam the service uses.
                patient.removeStructureSet(structureSet);
                StructureSetService.forgetStructureSetSnapshots(structureSet);
            }
            dicomProcessor.removeSopObjectFromPatient(
                patient.patientId,
                sopUid
            );
        },
        [
            patient,
            currentStructureSetId,
            dicomProcessor,
            unloadStructureSet,
            session,
            enqueueSnackbar,
        ]
    );

    const deleteRegistration = useCallback(
        (registration: RigidRegistration) => {
            if (!patient) {
                return;
            }
            // Resolve BEFORE removal so we still have its sopUid afterwards.
            const sopUid = registration.sopInstanceUid;
            const wasSelected = selectedRegistration?.sopUid === sopUid;
            // The instance the patient held, which is the one a grid row
            // fuses through.
            let removed = registration;
            if (session) {
                // With a session/tracker, delete by domain id through the
                // command-wrapped service (the legacy store path mutated
                // outside command() and now throws).
                const live =
                    session.patient.getRegistrationWithSopInstanceUid(sopUid);
                if (live) {
                    removed = live;
                    try {
                        session.registrationService.remove({ id: live.id });
                    } catch (e) {
                        // The command-wrapped remove throws a DomainError
                        // (e.g. ApprovedRegistrationError) instead of silently
                        // warning like the legacy store path. Tolerate it:
                        // surface a warning and abort the data delete so we
                        // never strip the DICOM object out from under a
                        // still-present registration. Non-domain errors fail
                        // fast.
                        if (e instanceof DomainError) {
                            enqueueSnackbar(e.message, { variant: 'warning' });
                            return;
                        }
                        throw e;
                    }
                }
                // live not found → already gone from the aggregate; nothing to
                // remove, just strip the SOP below.
            } else if (!patient.isTracked) {
                // No session and no tracker → a direct removal is safe
                // (markChanged is a no-op without a tracker) and keeps the
                // aggregate consistent with the DICOM store.
                patient.removeRegistration(registration);
            }
            // A tracked patient with no session is a teardown edge: a direct
            // removeRegistration would call markChanged outside a command() and
            // throw. Skip the aggregate mutation there (the entry is moot
            // without a session); the SOP strip below still runs.
            dicomProcessor.removeSopObjectFromPatient(
                patient.patientId,
                sopUid
            );
            // Clear selection if it pointed at the removed registration so it
            // does not dangle on a deleted entity.
            if (wasSelected) {
                setSelectedRegistration(null);
            }
            // No grid row may stay fused through a registration the patient
            // no longer holds: its tools would edit an object no save can
            // reach.
            getDatasetEnvironmentRegistry().clearFusionsThrough(removed);
        },
        [
            patient,
            session,
            dicomProcessor,
            selectedRegistration,
            setSelectedRegistration,
            enqueueSnackbar,
        ]
    );

    return useMemo(
        () => ({
            deleteImage,
            deleteRawSeries,
            deleteDose,
            deleteStructureSet,
            deleteRegistration,
            getAffectedStructureSets,
        }),
        [
            deleteImage,
            deleteRawSeries,
            deleteDose,
            deleteStructureSet,
            deleteRegistration,
            getAffectedStructureSets,
        ]
    );
}
